import { Injectable } from '@angular/core'

import { PolarisDataService } from '../polaris-service/polaris-data-service'
import { FundClassificationProcess } from './fund-classification-process'
import { FundFilterStateInitializer } from './fund-filter-state-initializer'
import { FundCategoryCode } from './models/fund-category-code.enum'
import { LowerLvlCategoryFundRuleModel } from './models/lower-lvl-category-fund-rule.model'
import {
  CashEquivalentMuniRule,
  CashEquivalentStableValueRule,
  CashEquivalentTaxableRule,
} from './rules/fund-rules/cash-equivalent-fund-rule'
import {
  EquityBlendRule,
  EquityDevRule,
  EquityEmergingRule,
  EquityGlobalRule,
  EquityGrowthRule,
  EquityINTRule,
  EquityLargeRule,
  EquityMidRule,
  EquityNonUSRule,
  EquitySmallRule,
  EquityStyleActiveRule,
  EquityStyleIndexRule,
  EquityUSMarketRule,
  EquityValueRule,
} from './rules/fund-rules/equity-fund-rule'
import {
  FixedIncomeBelowInvGradeRule,
  FixedIncomeDevRule,
  FixedIncomeEmergingRule,
  FixedIncomeGlobalRule,
  FixedIncomeIntermediateRule,
  FixedIncomeIntlRule,
  FixedIncomeInvGradeCorpRule,
  FixedIncomeLongRule,
  FixedIncomeMultiSectorRule,
  FixedIncomeMuniRule,
  FixedIncomeNonUSRule,
  FixedIncomeShortRule,
  FixedIncomeStyleActiveRule,
  FixedIncomeStyleIndexRule,
  FixedIncomeTreasuryAgencyRule,
  FixedIncomeUSRule,
} from './rules/fund-rules/fixed-income-fund-rule'
import {
  MultiAssetBalanceRule,
  MultiAssetINTRule,
  MultiAssetTargetAllocRule,
  MultiAssetTargetDateRule,
  MultiAssetUSMarketRule,
} from './rules/fund-rules/multi-asset-fund-rule'
import {
  DividendStrategyRule,
  ESGStrategyRule,
  FactorDrivenStrategyRule,
  SectorAndSpecialityRule,
} from './rules/fund-rules/strategy-fund-rule'

@Injectable({
  providedIn: 'root',
})
export class SearchFilter {
  filterInitialized = false
  filterServiceFailed = false
  constructor(
    private readonly stateInitializer: FundFilterStateInitializer,
    private readonly polarisDataService: PolarisDataService,
    private readonly fundRuleClassification: FundClassificationProcess,
  ) {}
  performFundClassification() {
    const polarisDataAry = this.polarisDataService.getProductList()
    if (!polarisDataAry) {
      this.filterServiceFailed = true
      return
    }
    this.registerCustomRules()
    // Classification of funds under respective filter categories
    polarisDataAry.forEach((productJson) => {
      this.fundRuleClassification.executeClassificationProcess(productJson)
    })
    this.filterInitialized = true
    this.stateInitializer.initializeState()
  }

  registerCustomRules() {
    // Equity
    this.registerEquityCustomRules()
    // Multi-Asset
    this.registerMultiAssetCustomRules()
    // Fixed Income
    this.registerFixedIncomeCustomRules()
    // cash equivalent
    this.registerCashEquivalentCustomRules()
    // Strategy
    this.registerStrategyCustomRules()
  }

  registerEquityCustomRules(): void {
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityUSMarketRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Us,
        FundCategoryCode.region,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityINTRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.International,
        FundCategoryCode.region,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityDevRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Developed,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityEmergingRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Emerging,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityGlobalRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Global,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityNonUSRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.NonUS,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityLargeRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Large,
        FundCategoryCode.size,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityMidRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Mid,
        FundCategoryCode.size,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquitySmallRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Small,
        FundCategoryCode.size,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityStyleIndexRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Index,
        FundCategoryCode.mgmtStyle,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityStyleActiveRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Active,
        FundCategoryCode.mgmtStyle,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityBlendRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Blend,
        FundCategoryCode.style,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityGrowthRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Growth,
        FundCategoryCode.style,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new EquityValueRule(),
        FundCategoryCode.Equity,
        FundCategoryCode.Value,
        FundCategoryCode.style,
      ),
    )
  }

  registerMultiAssetCustomRules(): void {
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new MultiAssetUSMarketRule(),
        FundCategoryCode.MultiAsset,
        FundCategoryCode.Us,
        FundCategoryCode.region,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new MultiAssetINTRule(),
        FundCategoryCode.MultiAsset,
        FundCategoryCode.International,
        FundCategoryCode.region,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new MultiAssetTargetDateRule(),
        FundCategoryCode.MultiAsset,
        FundCategoryCode.TargetDate,
        FundCategoryCode.category,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new MultiAssetTargetAllocRule(),
        FundCategoryCode.MultiAsset,
        FundCategoryCode.TargetAllocation,
        FundCategoryCode.category,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new MultiAssetBalanceRule(),
        FundCategoryCode.MultiAsset,
        FundCategoryCode.Balanced,
        FundCategoryCode.category,
      ),
    )
  }

  // eslint-disable-next-line max-statements
  registerFixedIncomeCustomRules(): void {
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeUSRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Us,
        FundCategoryCode.region,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeIntlRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.InternationalGlobal,
        FundCategoryCode.region,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeDevRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Developed,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeEmergingRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Emerging,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeGlobalRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Global,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeMultiSectorRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.MultiSector,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeNonUSRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.NonUS,
        FundCategoryCode.market,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeStyleIndexRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Index,
        FundCategoryCode.mgmtStyle,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeStyleActiveRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Active,
        FundCategoryCode.mgmtStyle,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeLongRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Long,
        FundCategoryCode.duration,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeIntermediateRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Intermediate,
        FundCategoryCode.duration,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeShortRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Short,
        FundCategoryCode.duration,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeBelowInvGradeRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.BelowInvGrade,
        FundCategoryCode.creditQuality,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeInvGradeCorpRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.InvGradeCorp,
        FundCategoryCode.creditQuality,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeMuniRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.Muni,
        FundCategoryCode.creditQuality,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FixedIncomeTreasuryAgencyRule(),
        FundCategoryCode.FixedIncome,
        FundCategoryCode.TreasuryAgency,
        FundCategoryCode.creditQuality,
      ),
    )
  }

  registerCashEquivalentCustomRules(): void {
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new CashEquivalentTaxableRule(),
        FundCategoryCode.CashEquivalent,
        FundCategoryCode.Taxable,
        FundCategoryCode.category,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new CashEquivalentMuniRule(),
        FundCategoryCode.CashEquivalent,
        FundCategoryCode.Muni,
        FundCategoryCode.category,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new CashEquivalentStableValueRule(),
        FundCategoryCode.CashEquivalent,
        FundCategoryCode.StableValue,
        FundCategoryCode.category,
      ),
    )
  }

  registerStrategyCustomRules(): void {
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new DividendStrategyRule(),
        FundCategoryCode.Dividend,
        FundCategoryCode.strategy,
        FundCategoryCode.strategy,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new ESGStrategyRule(),
        FundCategoryCode.ESGStrategy,
        FundCategoryCode.strategy,
        FundCategoryCode.strategy,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new FactorDrivenStrategyRule(),
        FundCategoryCode.FactorDrivenStrategy,
        FundCategoryCode.strategy,
        FundCategoryCode.strategy,
      ),
    )
    this.fundRuleClassification.registerRules(
      new LowerLvlCategoryFundRuleModel(
        new SectorAndSpecialityRule(),
        FundCategoryCode.SectorSpeciality,
        FundCategoryCode.strategy,
        FundCategoryCode.strategy,
      ),
    )
  }
}
